/*
  Lucía de Miguel · «Podría interesarte» en el carrito lateral (HTML de sections/vrs-cart-look.liquid).
  - Carga diferida: solo pide sugerencias cuando el bloque está cerca de verse (cajón abierto); nunca retrasa la
    apertura ni las actualizaciones del carrito, que siguen su camino normal.
  - Una petición por contenido del carrito (firma = productos distintos): abrir/cerrar o cambiar cantidades no repite.
  - En cada cambio el tema vuelve a pintar el cajón (morph) y deja aquí el esqueleto del servidor; si la firma no ha
    cambiado se restaura al momento lo ya cargado (antes de pintar, sin parpadeo).
  - «Añadir» solo existe si hay una única variante disponible; usa el mismo evento que product-form.
*/
import { CartLinesUpdateEvent } from '@shopify/events';

/** @type {Map<string, string>} firma → HTML ('' = sin sugerencias suficientes) */
const cache = new Map();
/** @type {Map<string, Promise<void>>} */
const pending = new Map();

class VrsCartLook extends HTMLElement {
  #observer = new MutationObserver(() => this.#sync());
  /** @type {IntersectionObserver | null} */
  #io = null;
  #near = false;

  connectedCallback() {
    this.#watch();
    this.#io = new IntersectionObserver(
      (entries) => {
        this.#near = entries.some((entry) => entry.isIntersecting);
        if (this.#near) this.#sync();
      },
      // Raíz = contenido desplazable del cajón (con la ventana como raíz, el recorte del cajón anula el margen).
      { root: this.closest('.cart-drawer__content'), rootMargin: '0px 0px 600px 0px' }
    );
    this.#io.observe(this);
    this.#sync();
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    this.#io?.disconnect();
  }

  #watch() {
    this.#observer.observe(this, { childList: true, attributes: true, attributeFilter: ['data-sig', 'hidden'] });
  }

  #sync() {
    const sig = this.dataset.sig || '';
    if (!sig) return this.#render(sig, '');
    if (cache.has(sig)) return this.#render(sig, cache.get(sig) || '');
    if (this.#near) this.#load(sig);
  }

  /**
   * @param {string} sig
   * @param {string} html
   */
  #render(sig, html) {
    const current = this.firstElementChild;
    if (current instanceof HTMLElement && current.dataset.vrsLookFor === sig && this.hidden === !html) return;
    this.#observer.disconnect();
    const wrap = document.createElement('div');
    wrap.dataset.vrsLookFor = sig;
    wrap.innerHTML = html;
    this.replaceChildren(wrap);
    this.hidden = !html;
    this.#watch();
  }

  /** @param {string} sig */
  async #load(sig) {
    if (!pending.has(sig)) {
      const params = new URLSearchParams({
        product_id: this.dataset.anchor || '',
        limit: '10',
        intent: 'related',
        section_id: 'vrs-cart-look',
      });
      const request = fetch(`${this.dataset.url}?${params}`, { headers: { Accept: 'text/html' } })
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
        .then((text) => {
          const body = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-vrs-look-body]');
          cache.set(sig, body && Number(body.getAttribute('data-count')) >= 3 ? body.innerHTML : '');
        })
        // Sin sugerencias si falla: el carrito sigue funcionando y no se reintenta en bucle.
        .catch(() => cache.set(sig, ''))
        .finally(() => pending.delete(sig));
      pending.set(sig, request);
    }
    await pending.get(sig);
    if (this.isConnected && this.dataset.sig === sig) this.#sync();
  }
}

if (!customElements.get('vrs-cart-look')) customElements.define('vrs-cart-look', VrsCartLook);

document.addEventListener('click', async (event) => {
  const target = /** @type {Element | null} */ (event.target);
  const btn = /** @type {HTMLButtonElement | null} */ (target?.closest?.('[data-vrs-look-add]') ?? null);
  if (!btn || btn.disabled) return;
  const card = btn.closest('[data-vrs-look-card]');
  const msg = card?.querySelector('[data-vrs-look-msg]');
  const id = Number(btn.dataset.vrsLookAdd);
  if (!id) return;
  btn.disabled = true;
  btn.setAttribute('aria-busy', 'true');
  btn.textContent = 'Añadiendo…';
  if (msg) msg.textContent = '';
  const sectionId = /** @type {HTMLElement | null} */ (btn.closest('cart-items-component'))?.dataset.sectionId || 'cart-drawer-section';
  const deferred = CartLinesUpdateEvent.createPromise();
  btn.dispatchEvent(
    new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: id, quantity: 1 }], promise: deferred.promise })
  );
  try {
    const res = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: sectionId, sections_url: window.location.pathname }),
    });
    const json = await res.json();
    if (!res.ok || json.status) throw new Error(json.description || json.message || 'No se ha podido añadir.');
    const cart = await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: json.sections, items: cart.items, itemCount: cart.item_count, source: 'vrs-cart-look', didError: false },
    });
  } catch (error) {
    deferred.reject(error);
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
    btn.textContent = 'Añadir';
    if (msg) msg.textContent = error instanceof Error ? error.message : 'No se ha podido añadir.';
  }
});
